'use client'

import { memo, useEffect, useState } from 'react'
import { GlassEffect, GlassFilter } from '@/components/ui/liquid-glass'
import { VoiceWaveBars } from '@/components/ui/voice-wave-bars'
import { NinxlDotWave } from './ninxl-dot-wave'
import { NinxlInkflowPasteDemo } from './ninxl-inkflow-paste-demo'

type InkflowDemoPhase = 'idle' | 'recording' | 'processing' | 'pasting' | 'done'

const CYCLE_MS = {
  idle: 1800,
  recording: 3600,
  processing: 2000,
  pasting: 2800,
  done: 1600,
} as const

const formatElapsed = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

type NinxlInkflowGlassChipProps = {
  phase: InkflowDemoPhase
  elapsed: number
}

const NinxlInkflowGlassChip = memo(function NinxlInkflowGlassChip({
  phase,
  elapsed,
}: NinxlInkflowGlassChipProps) {
  return (
    <div className="ninxl-inkflow-showcase-chip-host">
      <GlassFilter />
      <div className="ninxl-inkflow-showcase-chip-shell">
        {phase === 'idle' ? (
          <GlassEffect className="ninxl-inkflow-showcase-chip ninxl-inkflow-showcase-chip-idle" role="status">
            <span className="ninxl-inkflow-showcase-chip-mode">⇧ Dictate</span>
          </GlassEffect>
        ) : null}

        {phase === 'recording' ? (
          <GlassEffect
            className="ninxl-inkflow-showcase-chip ninxl-inkflow-showcase-chip-recording"
            role="status"
            aria-live="polite"
          >
            <span className="ninxl-inkflow-showcase-dot" aria-hidden />
            <span className="ninxl-inkflow-showcase-chip-mode">Dictate</span>
            <time className="ninxl-inkflow-showcase-chip-timer">{formatElapsed(elapsed)}</time>
          </GlassEffect>
        ) : null}

        {phase === 'processing' ? (
          <GlassEffect
            className="ninxl-inkflow-showcase-chip ninxl-inkflow-showcase-chip-processing"
            role="status"
            aria-live="polite"
          >
            <span className="ninxl-inkflow-showcase-chip-label">Processing</span>
            <span className="ninxl-inkflow-showcase-chip-dots" aria-hidden>
              <span />
              <span />
              <span />
            </span>
          </GlassEffect>
        ) : null}

        {phase === 'pasting' ? (
          <GlassEffect
            className="ninxl-inkflow-showcase-chip ninxl-inkflow-showcase-chip-processing"
            role="status"
            aria-live="polite"
          >
            <span className="ninxl-inkflow-showcase-chip-label">Pasting</span>
            <span className="ninxl-inkflow-showcase-chip-dots" aria-hidden>
              <span />
              <span />
              <span />
            </span>
          </GlassEffect>
        ) : null}

        {phase === 'done' ? (
          <GlassEffect className="ninxl-inkflow-showcase-chip ninxl-inkflow-showcase-chip-success" role="status">
            <span className="ninxl-inkflow-showcase-chip-success-icon" aria-hidden>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <polyline
                  points="20 6 9 17 4 12"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </GlassEffect>
        ) : null}
      </div>
    </div>
  )
})

export const NinxlInkflowShowcase = memo(function NinxlInkflowShowcase() {
  const [phase, setPhase] = useState<InkflowDemoPhase>('idle')
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    let cancelled = false
    const timeouts: number[] = []
    const order: InkflowDemoPhase[] = ['idle', 'recording', 'processing', 'pasting', 'done']

    const step = (index: number) => {
      const next = order[index % order.length]
      setPhase(next)
      if (next !== 'recording') setElapsed(0)

      timeouts.push(
        window.setTimeout(() => {
          if (!cancelled) step(index + 1)
        }, CYCLE_MS[next]),
      )
    }

    step(0)

    return () => {
      cancelled = true
      timeouts.forEach((id) => window.clearTimeout(id))
    }
  }, [])

  useEffect(() => {
    if (phase !== 'recording') return undefined

    const intervalId = window.setInterval(() => {
      setElapsed((current) => current + 1)
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [phase])

  const isListening = phase === 'recording'

  return (
    <div className="ninxl-inkflow-showcase" aria-label="InkFlow HUD preview">
      <div className="ninxl-inkflow-canvas">
        <NinxlDotWave />
        <div className="ninxl-inkflow-canvas-vignette" aria-hidden />
        <div className="ninxl-inkflow-canvas-ui">
          <NinxlInkflowGlassChip phase={phase} elapsed={elapsed} />
          <NinxlInkflowPasteDemo phase={phase} />
          <VoiceWaveBars active={isListening} bars={64} className="ninxl-inkflow-showcase-wave" />
        </div>
      </div>
    </div>
  )
})
